import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Card, Form, Modal, ListGroup, Badge, Spinner } from 'react-bootstrap';
import { FiUser, FiKey, FiLogOut, FiClock, FiShield, FiUserPlus, FiCopy, FiTrash2, FiChevronDown, FiChevronUp, FiDownload, FiMessageCircle } from 'react-icons/fi';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import { Header, BottomNav } from '../components/common';
import { useAuth } from '../context/AuthContext';
import { authApi, checkoutApi, adminApi, noticeApi, InviteCode } from '../api/client';

const levelLabels: Record<number, string> = {
  1: '관리자',
  2: '매니저',
  3: '스태프',
};

export const SettingsPage: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [selectedLevel, setSelectedLevel] = useState(3);
  const [showInviteCodeSection, setShowInviteCodeSection] = useState(false);
  const [showDownloadSection, setShowDownloadSection] = useState(false);
  const [downloadStartDate, setDownloadStartDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [downloadEndDate, setDownloadEndDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [isDownloading, setIsDownloading] = useState(false);
  const [showNoticeSection, setShowNoticeSection] = useState(false);
  const [noticeDate, setNoticeDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [noticeText, setNoticeText] = useState('');

  const dateStr = format(new Date(), 'yyyy-MM-dd');

  // 레벨 1, 2만 초대 코드 기능 접근 가능
  const canManageInviteCodes = user?.level === 1 || user?.level === 2;

  // 다운로드 권한: 레벨 1 또는 특정 사용자 ID
  const allowedDownloadUserIds = ['hjs5144', 'mdclean01'];
  const canDownload = user?.level === 1 || (user?.id && allowedDownloadUserIds.includes(user.id));

  // 생성 가능한 레벨 옵션
  const availableLevels = user?.level === 1 ? [1, 2, 3] : [3];

  const { data: checkoutData } = useQuery({
    queryKey: ['checkout-status', dateStr],
    queryFn: () => checkoutApi.getStatus(dateStr),
  });

  // 초대 코드 목록 조회
  const { data: inviteCodesData, isLoading: inviteCodesLoading } = useQuery({
    queryKey: ['invite-codes'],
    queryFn: () => adminApi.getInviteCodes(),
    enabled: canManageInviteCodes,
  });

  // 미사용 코드만 필터링
  const unusedCodes = inviteCodesData?.data.data?.codes.filter((c: InviteCode) => !c.isUsed) || [];

  // 초대 코드 생성
  const createCodeMutation = useMutation({
    mutationFn: (level: number) => adminApi.createInviteCodes(level, 1),
    onSuccess: (response) => {
      if (response.data.success) {
        queryClient.invalidateQueries({ queryKey: ['invite-codes'] });
        const code = response.data.data?.codes[0];
        toast.success(`코드 생성 완료: ${code}`);
      }
    },
    onError: () => {
      toast.error('코드 생성에 실패했습니다.');
    },
  });

  // 초대 코드 삭제
  const deleteCodeMutation = useMutation({
    mutationFn: (code: string) => adminApi.deleteInviteCode(code),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invite-codes'] });
      toast.success('코드가 삭제되었습니다.');
    },
    onError: () => {
      toast.error('삭제에 실패했습니다.');
    },
  });

  // 공지사항 조회
  const { data: noticeData, isLoading: noticeLoading } = useQuery({
    queryKey: ['notice', noticeDate],
    queryFn: () => noticeApi.getNotice(noticeDate),
    enabled: canManageInviteCodes && showNoticeSection,
  });

  // 공지사항 저장
  const saveNoticeMutation = useMutation({
    mutationFn: () => noticeApi.saveNotice(noticeDate, noticeText),
    onSuccess: (response) => {
      if (response.data.success) {
        queryClient.invalidateQueries({ queryKey: ['notice', noticeDate] });
        toast.success('공지사항이 저장되었습니다.');
      }
    },
    onError: () => {
      toast.error('공지사항 저장에 실패했습니다.');
    },
  });

  // 공지사항 데이터가 로드되면 noticeText 업데이트
  useEffect(() => {
    if (noticeData?.data.data?.notice) {
      setNoticeText(noticeData.data.data.notice.notice || '');
    } else {
      setNoticeText('');
    }
  }, [noticeData]);

  const handleCopyCode = (code: string) => {
    navigator.clipboard.writeText(code);
    toast.success('코드가 복사되었습니다.');
  };

  const handleDownloadExcel = async () => {
    if (!downloadStartDate || !downloadEndDate) {
      toast.error('시작일과 종료일을 선택해주세요.');
      return;
    }

    if (new Date(downloadStartDate) > new Date(downloadEndDate)) {
      toast.error('시작일이 종료일보다 늦을 수 없습니다.');
      return;
    }

    setIsDownloading(true);
    try {
      const apiBaseUrl = import.meta.env.VITE_API_URL || '/api';
      const response = await fetch(
        `${apiBaseUrl}/export/excel?startDate=${downloadStartDate}&endDate=${downloadEndDate}`,
        { credentials: 'include' }
      );

      if (!response.ok) {
        throw new Error('다운로드에 실패했습니다.');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `모두스테이_청소데이터_${downloadStartDate}_to_${downloadEndDate}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('다운로드가 완료되었습니다.');
    } catch {
      toast.error('다운로드에 실패했습니다.');
    } finally {
      setIsDownloading(false);
    }
  };

  const checkoutMutation = useMutation({
    mutationFn: () => checkoutApi.toggle(dateStr),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['checkout-status'] });
      toast.success(response.data.message || '상태가 변경되었습니다.');
    },
    onError: () => {
      toast.error('작업에 실패했습니다.');
    },
  });

  const changePasswordMutation = useMutation({
    mutationFn: () => authApi.changePassword(currentPassword, newPassword),
    onSuccess: (response) => {
      if (response.data.success) {
        toast.success('비밀번호가 변경되었습니다.');
        setShowPasswordModal(false);
        resetPasswordForm();
      } else {
        toast.error(response.data.message || '변경에 실패했습니다.');
      }
    },
    onError: () => {
      toast.error('비밀번호 변경에 실패했습니다.');
    },
  });

  const resetPasswordForm = () => {
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
  };

  const handleChangePassword = () => {
    if (newPassword !== confirmPassword) {
      toast.error('새 비밀번호가 일치하지 않습니다.');
      return;
    }
    if (newPassword.length < 4) {
      toast.error('비밀번호는 4자 이상이어야 합니다.');
      return;
    }
    changePasswordMutation.mutate();
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
    toast.success('로그아웃 되었습니다.');
  };

  const isCheckedOut = checkoutData?.data.data?.checkedOut || false;

  return (
    <div className="app-container">
      <Header title="설정" showProfile userName={user?.name} />

      <main className="main-content">
        {/* User Info */}
        <Card className="mb-3 shadow-sm">
          <Card.Header className="bg-white">
            <FiUser className="me-2" />
            내 정보
          </Card.Header>
          <ListGroup variant="flush">
            <ListGroup.Item className="d-flex justify-content-between">
              <span className="text-muted">아이디</span>
              <span className="fw-bold">{user?.id}</span>
            </ListGroup.Item>
            <ListGroup.Item className="d-flex justify-content-between">
              <span className="text-muted">이름</span>
              <span className="fw-bold">{user?.name}</span>
            </ListGroup.Item>
            <ListGroup.Item className="d-flex justify-content-between">
              <span className="text-muted">권한</span>
              <span>
                <FiShield className="me-1 text-primary" />
                {levelLabels[user?.level || 3]}
              </span>
            </ListGroup.Item>
          </ListGroup>
        </Card>

        {/* Checkout */}
        <Card className="mb-3 shadow-sm">
          <Card.Header className="bg-white d-flex justify-content-between align-items-center">
            <span>
              <FiClock className="me-2" />
              오늘의 근무
            </span>
            <small className="text-muted">{format(new Date(), 'yyyy년 MM월 dd일')}</small>
          </Card.Header>
          <Card.Body>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <span className="text-muted">퇴근 상태</span>
              <span className={`badge ${isCheckedOut ? 'bg-success' : 'bg-secondary'}`}>
                {isCheckedOut ? '퇴근 완료' : '근무 중'}
              </span>
            </div>
            <Button
              variant={isCheckedOut ? 'outline-warning' : 'success'}
              className="w-100"
              onClick={() => checkoutMutation.mutate()}
              disabled={checkoutMutation.isPending}
            >
              {isCheckedOut ? '퇴근 취소' : '퇴근하기'}
            </Button>
          </Card.Body>
        </Card>

        {/* Actions */}
        <Card className="mb-3 shadow-sm">
          <Card.Header className="bg-white">계정 관리</Card.Header>
          <ListGroup variant="flush">
            <ListGroup.Item
              action
              onClick={() => setShowPasswordModal(true)}
              className="d-flex align-items-center"
            >
              <FiKey className="me-2" />
              비밀번호 변경
            </ListGroup.Item>
            <ListGroup.Item
              action
              onClick={handleLogout}
              className="d-flex align-items-center text-danger"
            >
              <FiLogOut className="me-2" />
              로그아웃
            </ListGroup.Item>
          </ListGroup>
        </Card>

        {/* Notice - Level 1, 2 only */}
        {canManageInviteCodes && (
          <Card className="mb-3 shadow-sm">
            <Card.Header
              className="bg-white d-flex justify-content-between align-items-center"
              style={{
                cursor: 'pointer',
                borderRadius: showNoticeSection ? undefined : '0.375rem'
              }}
              onClick={() => setShowNoticeSection(!showNoticeSection)}
            >
              <span>
                <FiMessageCircle className="me-2" />
                공지사항 관리
              </span>
              {showNoticeSection ? <FiChevronUp /> : <FiChevronDown />}
            </Card.Header>
            {showNoticeSection && (
              <Card.Body>
                <div className="mb-3">
                  <Form.Label className="fw-bold">날짜 선택</Form.Label>
                  <Form.Control
                    type="date"
                    value={noticeDate}
                    onChange={(e) => setNoticeDate(e.target.value)}
                  />
                </div>
                <div className="mb-3">
                  <Form.Label className="fw-bold">공지 내용</Form.Label>
                  {noticeLoading ? (
                    <div className="text-center py-3">
                      <Spinner animation="border" size="sm" />
                    </div>
                  ) : (
                    <Form.Control
                      as="textarea"
                      rows={4}
                      value={noticeText}
                      onChange={(e) => setNoticeText(e.target.value)}
                      placeholder="공지사항을 입력하세요"
                    />
                  )}
                </div>
                <Button
                  variant="primary"
                  className="w-100"
                  onClick={() => saveNoticeMutation.mutate()}
                  disabled={saveNoticeMutation.isPending || noticeLoading}
                >
                  {saveNoticeMutation.isPending ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      저장 중...
                    </>
                  ) : (
                    '저장'
                  )}
                </Button>
              </Card.Body>
            )}
          </Card>
        )}

        {/* Invite Code - Level 1, 2 only */}
        {canManageInviteCodes && (
          <Card className="mb-3 shadow-sm">
            <Card.Header
              className="bg-white d-flex justify-content-between align-items-center"
              style={{
                cursor: 'pointer',
                borderRadius: showInviteCodeSection ? undefined : '0.375rem'
              }}
              onClick={() => setShowInviteCodeSection(!showInviteCodeSection)}
            >
              <span>
                <FiUserPlus className="me-2" />
                초대코드
              </span>
              {showInviteCodeSection ? <FiChevronUp /> : <FiChevronDown />}
            </Card.Header>
            {showInviteCodeSection && (
            <Card.Body>
              {/* 초대 코드 생성 */}
              <div className="mb-3">
                <label className="form-label fw-bold">
                  <FiUserPlus className="me-2" />
                  가입 코드 생성
                </label>
                <div className="d-flex gap-2">
                  <Form.Select
                    value={selectedLevel}
                    onChange={(e) => setSelectedLevel(Number(e.target.value))}
                    style={{ width: 'auto' }}
                  >
                    {availableLevels.map((level) => (
                      <option key={level} value={level}>
                        레벨 {level} ({levelLabels[level]})
                      </option>
                    ))}
                  </Form.Select>
                  <Button
                    variant="primary"
                    onClick={() => createCodeMutation.mutate(selectedLevel)}
                    disabled={createCodeMutation.isPending}
                  >
                    {createCodeMutation.isPending ? (
                      <Spinner animation="border" size="sm" />
                    ) : (
                      '생성'
                    )}
                  </Button>
                </div>
              </div>

              {/* 미사용 코드 목록 */}
              <div>
                <label className="form-label fw-bold mb-2">
                  미사용 코드 목록
                  <Badge bg="secondary" className="ms-2">{unusedCodes.length}</Badge>
                </label>
                {inviteCodesLoading ? (
                  <div className="text-center py-3">
                    <Spinner animation="border" size="sm" />
                  </div>
                ) : unusedCodes.length === 0 ? (
                  <div className="text-muted small">생성된 코드가 없습니다.</div>
                ) : (
                  <ListGroup variant="flush" className="border rounded">
                    {unusedCodes.map((inviteCode: InviteCode) => (
                      <ListGroup.Item
                        key={inviteCode.code}
                        className="d-flex justify-content-between align-items-center py-2"
                      >
                        <div>
                          <code className="fs-6 fw-bold">{inviteCode.code}</code>
                          <Badge
                            bg={inviteCode.level === 1 ? 'danger' : inviteCode.level === 2 ? 'warning' : 'info'}
                            className="ms-2"
                          >
                            {levelLabels[inviteCode.level]}
                          </Badge>
                        </div>
                        <div className="d-flex gap-1">
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            onClick={() => handleCopyCode(inviteCode.code)}
                            title="복사"
                          >
                            <FiCopy />
                          </Button>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => deleteCodeMutation.mutate(inviteCode.code)}
                            disabled={deleteCodeMutation.isPending}
                            title="삭제"
                          >
                            <FiTrash2 />
                          </Button>
                        </div>
                      </ListGroup.Item>
                    ))}
                  </ListGroup>
                )}
              </div>
            </Card.Body>
            )}
          </Card>
        )}

        {/* Download Section - Level 1 or specific users only */}
        {canDownload && (
          <Card className="mb-3 shadow-sm">
            <Card.Header
              className="bg-white d-flex justify-content-between align-items-center"
              style={{
                cursor: 'pointer',
                borderRadius: showDownloadSection ? undefined : '0.375rem'
              }}
              onClick={() => setShowDownloadSection(!showDownloadSection)}
            >
              <span>
                <FiDownload className="me-2" />
                데이터 다운로드
              </span>
              {showDownloadSection ? <FiChevronUp /> : <FiChevronDown />}
            </Card.Header>
            {showDownloadSection && (
              <Card.Body>
                <div className="mb-3">
                  <Form.Label className="fw-bold">기간 설정</Form.Label>
                  <div className="d-flex gap-2 align-items-center flex-wrap">
                    <Form.Control
                      type="date"
                      value={downloadStartDate}
                      onChange={(e) => setDownloadStartDate(e.target.value)}
                      style={{ width: 'auto' }}
                    />
                    <span>~</span>
                    <Form.Control
                      type="date"
                      value={downloadEndDate}
                      onChange={(e) => setDownloadEndDate(e.target.value)}
                      style={{ width: 'auto' }}
                    />
                  </div>
                </div>
                <Button
                  variant="success"
                  className="w-100"
                  onClick={handleDownloadExcel}
                  disabled={isDownloading}
                >
                  {isDownloading ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      다운로드 중...
                    </>
                  ) : (
                    <>
                      <FiDownload className="me-2" />
                      엑셀 다운로드
                    </>
                  )}
                </Button>
                <div className="mt-3 small text-muted">
                  <p className="mb-1">• 선택한 기간의 청소 데이터를 엑셀 파일로 다운로드합니다.</p>
                  <p className="mb-1">• 일일 통계, 직원별 통계, 개인별 상세 데이터가 포함됩니다.</p>
                  <p className="mb-0">• 데이터 양에 따라 시간이 소요될 수 있습니다.</p>
                </div>
              </Card.Body>
            )}
          </Card>
        )}
      </main>

      <BottomNav />

      {/* Password Change Modal */}
      <Modal
        show={showPasswordModal}
        onHide={() => {
          setShowPasswordModal(false);
          resetPasswordForm();
        }}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>비밀번호 변경</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>현재 비밀번호</Form.Label>
            <Form.Control
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              autoComplete="current-password"
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>새 비밀번호</Form.Label>
            <Form.Control
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              autoComplete="new-password"
            />
          </Form.Group>
          <Form.Group>
            <Form.Label>새 비밀번호 확인</Form.Label>
            <Form.Control
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              autoComplete="new-password"
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              setShowPasswordModal(false);
              resetPasswordForm();
            }}
          >
            취소
          </Button>
          <Button
            variant="primary"
            onClick={handleChangePassword}
            disabled={changePasswordMutation.isPending}
          >
            변경
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};
